import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { A } from '../../adminTheme';
import { orderApi, reservationApi, contactApi, menuApi, adminApi, galleryApi, couponApi } from '../../services/api';

const AdminLayout = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const base = slug ? `/${slug}` : '';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [counts, setCounts] = useState({ orders: 0, reservations: 0, messages: 0, menu: 0, tables: 0, coupons: 0, gallery: 0, users: 0 });

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [ordersRes, resRes, msgRes, menuRes, tablesRes, couponsRes, galleryRes, usersRes] = await Promise.allSettled([
          orderApi.getAll({ status: 'pending', limit: 1 }),
          reservationApi.getAll({ status: 'pending', limit: 1 }),
          contactApi.getAll(),
          menuApi.getAll({ limit: 1 }),
          adminApi.getTables(),
          couponApi.getAll(),
          galleryApi.getAll({ limit: 1 }),
          adminApi.getAdmins(),
        ]);
        setCounts({
          orders:       ordersRes.status === 'fulfilled' ? (ordersRes.value.data?.pagination?.total || 0) : 0,
          reservations: resRes.status === 'fulfilled'    ? (resRes.value.data?.pagination?.total || 0)    : 0,
          messages:     msgRes.status === 'fulfilled'    ? (msgRes.value.data?.data?.unread || 0)           : 0,
          menu:         menuRes.status === 'fulfilled'   ? (menuRes.value.data?.pagination?.total || 0)    : 0,
          tables:       tablesRes.status === 'fulfilled' ? (tablesRes.value.data?.data?.length || 0)       : 0,
          coupons:      couponsRes.status === 'fulfilled'? (couponsRes.value.data?.data?.length || 0)      : 0,
          gallery:      galleryRes.status === 'fulfilled'? (galleryRes.value.data?.data?.length || 0)       : 0,
          users:        usersRes.status === 'fulfilled'  ? (usersRes.value.data?.data?.length || 0)        : 0,
        });
      } catch {}
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    window.addEventListener('messages-updated', fetchCounts);
    return () => { clearInterval(interval); window.removeEventListener('messages-updated', fetchCounts); };
  }, []);

  const navItems = [
    { to: `${base}/admin`,              label: 'Dashboard',    icon: '🏠', end: true },
    { to: `${base}/admin/menu`,         label: 'Menu',         icon: '🍽️', badge: counts.menu     },
    { to: `${base}/admin/orders`,       label: 'Orders',       icon: '📦', badge: counts.orders       },
    { to: `${base}/admin/reservations`, label: 'Reservations', icon: '📅', badge: counts.reservations },
    { to: `${base}/admin/tables`,       label: 'Table QR',     icon: '🪑', badge: counts.tables   },
    { to: `${base}/admin/coupons`,      label: 'Coupons',      icon: '🎟️', badge: counts.coupons  },
    { to: `${base}/admin/reviews`,      label: 'Reviews',      icon: '⭐'            },
    { to: `${base}/admin/messages`,     label: 'Messages',     icon: '💬', badge: counts.messages     },
    { to: `${base}/admin/gallery`,      label: 'Gallery',      icon: '🖼️', badge: counts.gallery  },
    { to: `${base}/admin/users`,        label: 'Users',        icon: '👥', badge: counts.users    },
    { to: `${base}/admin/settings`,     label: 'Settings',     icon: '⚙️'            },
  ];

  const pageTitles = {
    [`${base}/admin`]:              'Dashboard',
    [`${base}/admin/menu`]:         'Menu Management',
    [`${base}/admin/orders`]:       'Orders',
    [`${base}/admin/reservations`]: 'Reservations',
    [`${base}/admin/tables`]:       'Table QR Codes',
    [`${base}/admin/coupons`]:      'Coupons',
    [`${base}/admin/gallery`]:      'Gallery',
    [`${base}/admin/users`]:        'Users & Roles',
    [`${base}/admin/settings`]:     'Settings',
  };

  const handleLogout = () => setShowLogoutConfirm(true);
  const confirmLogout = () => {
    logout(slug);
    toast.success('Signed out');
  };

  const currentTitle = pageTitles[location.pathname] || 'Admin';

  const SidebarContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: A.surface1 }}>

      {/* Logo */}
      <div style={{ padding: '24px 20px 20px', borderBottom: `1px solid ${A.border1}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
            overflow: 'hidden', boxShadow: '0 2px 8px rgba(201,100,40,0.3)',
          }}>
            <img
              src="/favicon-512.png"
              alt="SwadZone Logo"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
          <div>
            <p style={{ fontFamily: '"Playfair Display",serif', fontWeight: 700, color: A.text1, fontSize: 14, lineHeight: 1.2 }}>
              Swad<span style={{ color: A.accent }}>Zone</span>
            </p>
            <p style={{ color: A.text4, fontSize: 11 }}>Management Panel</p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
        <p style={{ color: A.text4, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', padding: '0 8px 10px' }}>
          Menu
        </p>
        {navItems.map(item => (
          <NavLink key={item.to} to={item.to} end={item.end}
            onClick={() => setSidebarOpen(false)}
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '9px 12px', borderRadius: 10, marginBottom: 2,
              textDecoration: 'none', transition: 'all 0.15s',
              background: isActive ? A.accentSoft : 'transparent',
              border: `1px solid ${isActive ? A.accentBorder : 'transparent'}`,
              color: isActive ? A.accent : A.text3,
              fontWeight: isActive ? 600 : 400,
              fontSize: 14,
            })}>
            <span style={{ fontSize: 15 }}>{item.icon}</span>
            <span style={{ flex: 1 }}>{item.label}</span>
            {item.badge > 0 && (
              <span style={{ background: A.accent, color: '#fff', fontSize: 10, fontWeight: 800, borderRadius: 99, padding: '1px 7px', lineHeight: 1.7, flexShrink: 0 }}>
                {item.badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div style={{ padding: '12px', borderTop: `1px solid ${A.border1}` }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 12px', borderRadius: 10, marginBottom: 6,
          background: A.surface3, border: `1px solid ${A.border1}`,
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
            background: `linear-gradient(135deg, ${A.accent}, ${A.accentHover})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontWeight: 700, fontSize: 12,
          }}>{admin?.name?.[0]}</div>
          <div style={{ minWidth: 0 }}>
            <p style={{ color: A.text1, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{admin?.name}</p>
            <p style={{ color: A.text4, fontSize: 11, textTransform: 'capitalize' }}>{admin?.role}</p>
          </div>
        </div>

        <button onClick={handleLogout}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%',
            padding: '8px 12px', borderRadius: 10, border: 'none',
            background: 'transparent', cursor: 'pointer', fontSize: 13,
            color: A.red, fontFamily: 'inherit', transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = A.redBg}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          ↩ Sign Out
        </button>

        <a href={`${base}`} target="_blank" rel="noreferrer"
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
            borderRadius: 10, color: A.text3, fontSize: 13, textDecoration: 'none',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = A.surface3}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          ↗ View Website
        </a>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: A.bg, display: 'flex', fontFamily: '"DM Sans",sans-serif' }}>

      {/* Desktop sidebar */}
      <aside style={{
        width: 220, flexShrink: 0, position: 'fixed', top: 0, left: 0, bottom: 0,
        borderRight: `1px solid ${A.border1}`, zIndex: 40,
        boxShadow: '2px 0 12px rgba(0,0,0,0.04)',
      }} className="admin-sidebar-desktop">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50 }} />
            <motion.aside
              initial={{ x: -220 }} animate={{ x: 0 }} exit={{ x: -220 }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              style={{
                position: 'fixed', top: 0, left: 0, bottom: 0, width: 220,
                borderRight: `1px solid ${A.border1}`, zIndex: 60,
                boxShadow: '4px 0 24px rgba(0,0,0,0.12)',
              }}>
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column' }} className="admin-main-content">

        {/* Topbar */}
        <header style={{
          background: A.surface1,
          borderBottom: `1px solid ${A.border1}`,
          padding: '0 28px', height: 60,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, zIndex: 30,
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button onClick={() => setSidebarOpen(true)}
              className="admin-hamburger"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, display: 'none', flexDirection: 'column', gap: 4, borderRadius: 8 }}>
              {[0,1,2].map(i => (
                <span key={i} style={{ display: 'block', width: 18, height: 2, background: A.text3, borderRadius: 2 }} />
              ))}
            </button>
            <div>
              <h1 style={{ fontFamily: '"Playfair Display",serif', fontSize: 20, fontWeight: 700, color: A.text1, margin: 0, lineHeight: 1 }}>
                {currentTitle}
              </h1>
            </div>
          </div>

          {/* Right */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 14px', borderRadius: 99,
              background: A.surface3, border: `1px solid ${A.border1}`,
            }}>
              <div style={{
                width: 26, height: 26, borderRadius: '50%',
                background: `linear-gradient(135deg, ${A.accent}, ${A.accentHover})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, color: 'white',
              }}>{admin?.name?.[0]}</div>
              <span style={{ color: A.text2, fontSize: 13, fontWeight: 500 }}>{admin?.name}</span>
              <span style={{
                fontSize: 10, padding: '2px 8px', borderRadius: 99, textTransform: 'capitalize',
                background: A.accentSoft, color: A.accent, fontWeight: 600,
              }}>{admin?.role}</span>
            </div>
          </div>
        </header>

        {/* Page */}
        <div style={{ flex: 1, padding: '28px 28px', maxWidth: '100%' }}>
          <Outlet />
        </div>
      </main>

      {/* Logout Confirm Modal */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ position: 'absolute', inset: 0, background: 'rgba(26,15,0,0.7)', backdropFilter: 'blur(4px)' }}
              onClick={() => setShowLogoutConfirm(false)} />
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
              style={{ position: 'relative', background: '#1a0f00', border: '1px solid #3d2010', borderRadius: 22, padding: 32, width: '100%', maxWidth: 340, textAlign: 'center', boxShadow: '0 32px 80px rgba(0,0,0,0.4)' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>👋</div>
              <h3 style={{ fontFamily: '"Playfair Display",serif', fontSize: 18, fontWeight: 700, color: '#fde0cc', margin: '0 0 8px' }}>Sign Out?</h3>
              <p style={{ fontSize: 13, color: '#8b6347', marginBottom: 24 }}>Are you sure you want to sign out?</p>
              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={() => setShowLogoutConfirm(false)}
                  style={{ flex: 1, padding: 11, borderRadius: 12, border: '1px solid #3d2010', background: 'transparent', color: '#a07858', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Cancel
                </button>
                <button onClick={confirmLogout}
                  style={{ flex: 1, padding: 11, borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#c96428,#9a4a1e)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 14px rgba(201,100,40,0.3)' }}>
                  Sign Out
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <style>{`
        @media (min-width: 1024px) {
          .admin-sidebar-desktop { display: block !important; }
          .admin-main-content { margin-left: 220px; }
          .admin-hamburger { display: none !important; }
        }
        @media (max-width: 1023px) {
          .admin-sidebar-desktop { display: none !important; }
          .admin-hamburger { display: flex !important; }
        }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: ${A.bg}; }
        ::-webkit-scrollbar-thumb { background: ${A.border1}; border-radius: 99px; }
      `}</style>
    </div>
  );
};

export default AdminLayout;
